import {
  useV2ListSurveysForAccountQuery,
  V2ProjectResource,
  V2QuestionnaireResource,
} from "@generated";
import { CompletedSurvey } from "../types";
import { getFormattedDate } from "@utils/helperFunctions";

const useListCompletedSurveys = ({
  pageSize,
  pageNumber,
  filterParams,
  sortBy,
  sortOrder,
  projectId,
}) => {
  let customError: string | null = null; // Declare customError
  let pageMeta = {
    recordCount: 0,
    pageCount: 0,
  };

  const status = ["completed", "applied", "revised"];

  const { data, error, isLoading, refetch, isFetching } =
    useV2ListSurveysForAccountQuery({
      filter: {
        ...filterParams,
        status: projectId ? [...status, "pending", "shared"] : status,
        "project-id": projectId || undefined,
      },
      page: {
        number: pageNumber,
        size: pageSize,
      },
      //@ts-ignore
      include: ["project", "questionnaire", "project-survey"],
      sort: sortBy
        ? [sortOrder == "asc" ? sortBy : `-${sortBy}`]
        : ["-updated_at"],
    });

  let completedSurveys: CompletedSurvey[] = [];

  if (data?.data) {
    try {
      completedSurveys = data.data.map((survey) => {
        if (!survey.id) {
          throw new Error("Missing survey.id");
        }
        if (!survey.attributes.name) {
          throw new Error("Missing survey.attributes.name");
        }

        const projectId = String(survey.relationships.project?.data?.id);
        const relatedProject = data?.included?.find(
          (p) => p.type == "projects" && p.id === projectId
        ) as V2ProjectResource | undefined;
        const questionnaireId = survey.relationships.questionnaire.data?.id;
        const relatedQuestionnaire = data?.included?.find(
          (q) => q.type === "questionnaires" && q.id === questionnaireId
        ) as V2QuestionnaireResource | undefined;

        return {
          id: String(survey.id),
          checked: false,
          attributes: {
            name: survey.attributes.name,
            project: relatedProject
              ? {
                  id: String(relatedProject.id),
                  name: relatedProject?.attributes?.["project-name"],
                }
              : undefined,
            completedBy: survey.attributes?.["completed-by"]
              ? survey.attributes?.["completed-by"]
              : survey.attributes?.["contact-name"]
              ? survey.attributes?.["contact-name"]
              : "",
            completedOn: survey.attributes?.["updated-at"]
              ? getFormattedDate(new Date(survey.attributes["updated-at"]))
              : "",
            tags: relatedQuestionnaire?.["attributes"]?.["tag-list"] || [],
            questionnaireId,
            projectId,
            projectSurveyId:
              survey?.relationships?.["project-survey"]?.data?.id || undefined,
            status: survey.attributes?.status || "",
          },
        };
      });

      if (!data.meta) {
        throw new Error("Missing data.meta");
      }

      if (!data.meta["record-count"]) {
        throw new Error('Missing data.meta["record-count"]');
      }

      if (!data.meta["page-count"]) {
        throw new Error('Missing data.meta["page-count"]');
      }
      pageMeta = {
        recordCount: data.meta["record-count"],
        pageCount: data.meta["page-count"],
      };
    } catch (err) {
      if (err instanceof Error) {
        customError = err.message;
      } else {
        customError = "An unknown error occurred";
      }
    }
  }
  return {
    completedSurveys,
    isLoading,
    error: error ?? customError,
    pageMeta,
    refetch,
    isFetching,
  };
};
export default useListCompletedSurveys;
